<template>
  <AppCartSidebarRow
    v-if="isVisible"
    :title="title"
    :image-url="imageUrl"
    :description-html="descriptionHtml"
    :description="descriptionHtml ? null : rewardUnlockedDescription"
    is-free
    :is-arriving="isArriving"
  >
    <template #price>
      <span>{{ cartSidebarContent.freeRewardPriceLabel }}</span>
    </template>
  </AppCartSidebarRow>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from 'vue'

import { useCatalogStore } from '@/stores/catalog'

import { useRewards } from '@/composables/useRewards'
import {
  isRewardCartItemEnabled,
  rewardCartItemDescriptionHtml,
  rewardCartItemImageUrl,
  rewardCartItemTitle
} from '@/lib/rewardCartItemContent'

import { RewardTierState } from '@/types/RewardTier'

import { PromotionType } from '@VelocitySquared/sunny16-commerce-dataobjects/enums'

import AppCartSidebarRow from './AppCartSidebarRow.vue'
import { cartSidebarContent } from '@/content/cartSidebar'

/**
 * Cart-flyout line item for a reward that has **no cart bundle behind it** — Gap 5, Figma
 * `15980:30135`. Named for the concept rather than the case so it matches the Contentful vocabulary it
 * reads (`showSynthesizedRewardCartItem`, `rewardCartItem*`).
 *
 * Some promotions grant a bundle, which arrives in `cartBundles` and renders itself. Others waive a
 * cost — free shipping, order discounts — and produce no cart line at all. This reads those from the
 * reward ladder and gives them a row, so the customer sees everything they earned in one list.
 *
 * Presentation comes entirely from the promotion's Contentful entry via `rewardCartItemContent`, gated
 * on `showSynthesizedRewardCartItem`. The gate is **opt-in** — until it is switched on for a promotion,
 * no row renders. Intended behaviour, not a regression.
 *
 * Deliberately **flyout-only**: checkout renders the real shipping line from the prepared order, and a
 * second synthesized row there would double it up. Enforced by where this is mounted (`AppCartSidebar`
 * only) rather than by a route check.
 *
 * ---
 *
 * **Scoped to `ShippingDiscount` today, and the narrow filter is load-bearing.** Moving the content to
 * Contentful generalised everything except the amount and the FREE pill, and both block on data that
 * does not exist:
 *
 * - `RewardPromotionStorefrontDto` carries `promotionId`, `nameInternal`, `type`, `minimumSubtotal`,
 *   `additionalBundleId` and `attributes` — and **no discount figure**. So a `SubtotalDiscount` reward
 *   has nothing to render where this shows `$0.00`. Fixing that is a DTO addition; this feature has none.
 * - A green FREE pill reads wrong on "$20 off", where the order is cheaper rather than the item free,
 *   and no board frame designs that treatment.
 *
 * Widening the filter without solving both would render `$0.00` beside a FREE pill for a discount —
 * worse output than no row, and reachable just by an author switching the gate on.
 *
 * `$0.00` is a literal, not a computed figure: the cart carries no shipping totals until checkout
 * prepares the order, so there is nothing to derive. It is deliberately not authorable either — an
 * authored price can drift from what is actually charged.
 */
export default defineComponent({
  name: 'AppCartSidebarSynthesizedRewardCartItem',
  components: { AppCartSidebarRow },
  props: {
    /**
     * Promotion ids whose reward rows the unlock sequence is holding back until its reveal beat (A2b).
     *
     * This row is the one reward line the flyout renders from the *ladder* rather than from
     * `cartBundles`, so its caller cannot withhold it by filtering a list — it has to be told. Both
     * lists are ids rather than booleans because only this component knows which tier it is drawing.
     */
    withheldTierIds: {
      type: Array as PropType<readonly string[]>,
      default: () => []
    },
    /** Promotion ids the reveal beat has just brought in — plays the arrival flash. */
    arrivingTierIds: {
      type: Array as PropType<readonly string[]>,
      default: () => []
    }
  },
  setup(props) {
    const { tiers } = useRewards()
    const catalogStore = useCatalogStore()

    const tier = computed(() =>
      tiers.value.find(
        (candidate) =>
          candidate.type === PromotionType.ShippingDiscount &&
          candidate.state === RewardTierState.Unlocked
      )
    )

    const promotion = computed(() =>
      tier.value ? catalogStore.promotionContent(tier.value.promotionId) : undefined
    )

    const promotionId = computed(() => tier.value?.promotionId ?? null)

    return {
      cartSidebarContent,
      // No tier means no promotion, and the gate reads `undefined` as off — so this covers both. The
      // sequence adds one more reason to render nothing: its own crossing granted this row, and the
      // reveal beat has not arrived yet.
      isVisible: computed(
        () =>
          isRewardCartItemEnabled(promotion.value) &&
          !(promotionId.value && props.withheldTierIds.includes(promotionId.value))
      ),
      isArriving: computed(
        () => !!promotionId.value && props.arrivingTierIds.includes(promotionId.value)
      ),
      title: computed(() => rewardCartItemTitle(promotion.value, tier.value?.label ?? '')),
      imageUrl: computed(() => rewardCartItemImageUrl(promotion.value)),
      descriptionHtml: computed(() => rewardCartItemDescriptionHtml(promotion.value)),
      rewardUnlockedDescription: cartSidebarContent.rewardUnlockedDescription
    }
  }
})
</script>
